/**
 * Required-field validation for the Create Invoice form (OR-1449).
 *
 * Only covers what the user can actually fix on this screen. Everything
 * pre-filled and locked is the backend's to validate — it refuses the
 * create with a 422 naming the missing precondition, and the Invoice
 * page's gate banner warns before the user ever gets here.
 *
 * ⚠️ Three of these fields are collected but **not yet transmitted**:
 * `dueDate`, `accountManager` and `accountEmail` have no home in
 * `CreateAscendInvoiceRequest` today (the backend computes the due date as
 * today + 30 days, and the account manager has no field at all). They are
 * validated anyway because the approved design marks them required and
 * they are expected to reach the payload once the backend accepts them —
 * see the gap list. Until then, filling them changes nothing downstream.
 */

export interface InvoiceFormValues {
    /** Canonical digits from the Selected Premium field. Ascend's Billable
     *  requires a positive premium, so a zero here guarantees a failed
     *  sync — the design blocks Generate on it explicitly. */
    premium: string;
    phone: string;
    billingBrokerId: string | null;
    /** The chosen broker's email, or null when the record has none. Ascend
     *  keys the Account User on it, so a broker without one can never be
     *  provisioned and the create would fail after the fact. */
    billingBrokerEmail: string | null;
    /** Pre-filled from the broker, but the user owns the final value: a
     *  broker row can have a display name and still null first/last name
     *  (OR-1555), and Ascend rejects blanks with an opaque ERR_VALIDATION. */
    billingFirstName: string;
    billingLastName: string;
    accountManager: string;
    accountEmail: string;
    dueDate: string;
    coverageType: string | null;
}

/** Field id → message. Empty means the form can be submitted. */
export type InvoiceErrors = Record<string, string>;

export const REQUIRED_MESSAGE = "This field is required";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function blank(value: string | null | undefined): boolean {
    return value == null || value.trim() === "";
}

/** `MM/DD/YYYY` with a real calendar date behind it. */
export function isCompleteUsDate(value: string): boolean {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
    if (!m) return false;
    const [, mm, dd, yyyy] = m;
    const month = Number(mm);
    const day = Number(dd);
    if (month < 1 || month > 12 || day < 1) return false;
    // Day 0 of the next month is the last day of this one.
    return day <= new Date(Number(yyyy), month, 0).getDate();
}

export function validateInvoiceForm(values: InvoiceFormValues): InvoiceErrors {
    const errors: InvoiceErrors = {};

    const premiumValue = Number(values.premium);
    if (blank(values.premium) || !Number.isFinite(premiumValue) || premiumValue <= 0) {
        errors.premium = "Premium must be greater than $0";
    }

    if (blank(values.phone)) errors.phone = REQUIRED_MESSAGE;
    // Ascend requires a 10-digit phone (`5555555555`) and rejects the whole
    // Account creation with an opaque ERR_VALIDATION otherwise — caught
    // live against the sandbox (9 digits → 502).
    else if (values.phone.replace(/\D/g, "").length !== 10) errors.phone = "Enter a 10-digit phone number";
    // A selected broker without an email is as unusable as no broker: the
    // Account User payload needs one, so the Email field itself is flagged.
    if (blank(values.billingBrokerId) || blank(values.billingBrokerEmail)) errors.billingBrokerId = REQUIRED_MESSAGE;
    // Checked on the resolved values, not the broker row, so a blank name is
    // caught here with an inline message instead of a failed request. Still
    // worth having once the backend derives a fallback from `broker.name`:
    // that only helps when `name` itself is non-blank.
    if (blank(values.billingFirstName)) errors.billingFirstName = REQUIRED_MESSAGE;
    if (blank(values.billingLastName)) errors.billingLastName = REQUIRED_MESSAGE;
    if (blank(values.accountManager)) errors.accountManager = REQUIRED_MESSAGE;

    if (blank(values.accountEmail)) errors.accountEmail = REQUIRED_MESSAGE;
    else if (!EMAIL_RE.test(values.accountEmail.trim())) errors.accountEmail = "Enter a valid email address";

    if (blank(values.dueDate)) errors.dueDate = REQUIRED_MESSAGE;
    else if (!isCompleteUsDate(values.dueDate)) errors.dueDate = "Enter a valid date";

    if (blank(values.coverageType)) errors.coverageType = REQUIRED_MESSAGE;

    return errors;
}

/** The count the error toast reports. */
export function errorCount(errors: InvoiceErrors): number {
    return Object.keys(errors).length;
}
